import React from 'react';
import { createMemoryRouter, RouterProvider } from 'react-router-dom';

import { AppContext } from './AppContext';
import About from './components/About.jsx';
import Achievements from './components/Achievements.jsx';
import AvatarPicker from './components/Avatar/AvatarPicker.jsx';
import ComputerMenu from './components/ComputerMenu.jsx';
import Game from './components/Game.jsx';
import HowToPlay from './components/HowToPlay.jsx';
import Loading from './components/Loading.jsx';
import MainMenu from './components/MainMenu.jsx';
import MoreGames from './components/MoreGames.jsx';
import OnlineMenu from './components/OnlineMenu.jsx';
import OnlineWaitingRoom from './components/OnlineWaitingRoom.jsx';
import Profile from './components/Profile.jsx';
import Root from './components/Root.jsx';
import Settings from './components/Settings.jsx';
import Statistics from './components/Statistics.jsx';
import Store from './components/Store.jsx';
import Test from './components/Test.jsx';

const router = createMemoryRouter([
	{
		path: '/',
		element: <Root />,
		children: [
			{
				index: true,
				path: '/',
				element: <MainMenu />,
			},
			{
				path: '/MainMenu',
				element: <MainMenu />,
			},
			{
				path: 'ComputerMenu',
				element: <ComputerMenu />,
			},
			{
				path: 'About',
				element: <About />,
			},
			{
				path: 'Achievements',
				element: <Achievements />,
			},
			{
				path: 'Game/:mode/:level',
				element: <Game />,
			},
			{
				path: 'Statistics',
				element: <Statistics />,
			},
			{
				path: 'HowToPlay',
				element: <HowToPlay />,
			},
			{
				path: 'Settings',
				element: <Settings />,
			},
			{
				path: 'Loading',
				element: <Loading />,
			},
			{
				path: 'MoreGames',
				element: <MoreGames />,
			},
			{
				path: 'Test',
				element: <Test />,
			},
			{
				path: 'Store',
				element: <Store />,
			},
			{
				path: 'Profile',
				element: <Profile />,
			},
			{
				path: 'Avatar',
				element: <AvatarPicker />,
			},
			{
				path: 'Online',
				element: <OnlineMenu />,
			},
			{
				path: 'Online/:mode',
				element: <OnlineWaitingRoom />,
			},
		],
	},
]);

const App = ({ application }) => {
	const app = application;
	const [, setForceUpdate] = React.useState(0); //used to force update the state

	const oldAppSetMuted = app.setIsMuted;
	const oldAppSetRemoveAds = app.setRemoveAds;
	const oldAppOnBuy = app.iap.onBuy;

	app.iap.onBuy = (productId) => {
		oldAppOnBuy.call(app, productId);
		setForceUpdate(forceUpdate => forceUpdate + 1);
	};

	app.setRemoveAds = (shouldRemoveAds) => {
		oldAppSetRemoveAds.call(app, shouldRemoveAds);
		setForceUpdate(forceUpdate => forceUpdate + 1);
	};

	app.setIsMuted = (isMuted) => {
		oldAppSetMuted.call(app, isMuted);
		setForceUpdate(forceUpdate => forceUpdate + 1);
	};

	app.onNavigate = (path) => {
		window.location.hash = path;
		router.navigate('/' + path);
	};

	const hashHandler = React.useCallback(() => {
		const path = window.location.hash.substring(1); //remove hash
		app.navigate(path);
	}, [app]);

	React.useEffect(() => {
		window.addEventListener('hashchange', hashHandler);
		window.location.hash = ''; //init hash
		router.navigate('/' + app.pageId);

		return () => {
			window.removeEventListener('hashchange', hashHandler);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<AppContext.Provider value={app}>
			<RouterProvider router={router} fallbackElement={<p>Loading...</p>} />
		</AppContext.Provider>
	);
};

if (import.meta.hot) {
	import.meta.hot.dispose(() => router.dispose());
}

export default React.memo(App);
